import { useState, useRef, useEffect } from "react";
import { createStory } from "../../../functions/story";
import { uploadStoryMedia } from "../../../functions/uploadMedia";

const backgrounds = [
  { id: 1, style: "linear-gradient(135deg, #1877f2 0%, #42b72a 100%)" },
  { id: 2, style: "linear-gradient(135deg, #833ab4 0%, #fd1d1d 50%, #fcb045 100%)" },
  { id: 3, style: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)" },
  { id: 4, style: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)" },
  { id: 5, style: "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)" },
  { id: 6, style: "linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)" },
  { id: 7, style: "linear-gradient(135deg, #fa709a 0%, #fee140 100%)" },
  { id: 8, style: "linear-gradient(135deg, #30cfd0 0%, #330867 100%)" },
  { id: 9, style: "linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)" },
  { id: 10, style: "#1877f2" },
  { id: 11, style: "#42b72a" },
  { id: 12, style: "#f02849" },
  { id: 13, style: "#000000" },
  { id: 14, style: "#6c757d" },
];

// Visual effects/filters for photos and videos
const effects = [
  { id: "none", name: "Original", filter: "none" },
  { id: "grayscale", name: "P&B", filter: "grayscale(100%)" },
  { id: "sepia", name: "Sépia", filter: "sepia(100%)" },
  { id: "vintage", name: "Vintage", filter: "sepia(50%) contrast(85%) brightness(90%)" },
  { id: "warm", name: "Quente", filter: "sepia(30%) saturate(140%) brightness(105%)" },
  { id: "cool", name: "Frio", filter: "saturate(80%) hue-rotate(180deg) brightness(95%)" },
  { id: "bright", name: "Brilhante", filter: "brightness(120%) contrast(105%)" },
  { id: "dramatic", name: "Dramático", filter: "contrast(140%) brightness(90%)" },
  { id: "fade", name: "Desbotado", filter: "contrast(80%) brightness(110%) saturate(80%)" },
  { id: "vivid", name: "Vívido", filter: "saturate(150%) contrast(110%)" },
  { id: "retro", name: "Retrô", filter: "sepia(40%) hue-rotate(-10deg) saturate(120%)" },
  { id: "noir", name: "Noir", filter: "grayscale(100%) contrast(120%) brightness(90%)" },
];

// Music/sound library
const musicLibrary = [
  { id: 1, title: "Chill Vibes", artist: "Relaxing Beats", duration: "0:30", url: "/sounds/chill-vibes.mp3" },
  { id: 2, title: "Happy Day", artist: "Upbeat Music", duration: "0:30", url: "/sounds/happy-day.mp3" },
  { id: 3, title: "Epic Moment", artist: "Cinematic", duration: "0:30", url: "/sounds/epic.mp3" },
  { id: 4, title: "Acoustic Morning", artist: "Guitar Vibes", duration: "0:30", url: "/sounds/acoustic.mp3" },
  { id: 5, title: "Party Time", artist: "Dance Beats", duration: "0:30", url: "/sounds/party.mp3" },
  { id: 6, title: "Romantic", artist: "Love Songs", duration: "0:30", url: "/sounds/romantic.mp3" },
  { id: 7, title: "Motivation", artist: "Inspirational", duration: "0:30", url: "/sounds/motivation.mp3" },
  { id: 8, title: "Lo-Fi Study", artist: "Chill Hop", duration: "0:30", url: "/sounds/lofi.mp3" },
];

// Story templates/models
const templates = [
  {
    id: 1,
    name: "Pergunta",
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    textStyle: { fontSize: "24px", textAlign: "center" },
    placeholder: "Faça uma pergunta...",
    icon: "❓"
  },
  {
    id: 2,
    name: "Citação",
    background: "linear-gradient(135deg, #f5af19 0%, #f12711 100%)",
    textStyle: { fontSize: "22px", fontStyle: "italic", textAlign: "center" },
    placeholder: '"Sua citação inspiradora aqui..."',
    icon: "💬"
  },
  {
    id: 3,
    name: "Contagem",
    background: "linear-gradient(135deg, #11998e 0%, #38ef7d 100%)",
    textStyle: { fontSize: "48px", fontWeight: "bold", textAlign: "center" },
    placeholder: "3... 2... 1...",
    icon: "🔢"
  },
  {
    id: 4,
    name: "Enquete",
    background: "linear-gradient(135deg, #8E2DE2 0%, #4A00E0 100%)",
    textStyle: { fontSize: "20px", textAlign: "center" },
    placeholder: "Sim ou Não?",
    icon: "📊"
  },
  {
    id: 5,
    name: "Anúncio",
    background: "linear-gradient(135deg, #EB3349 0%, #F45C43 100%)",
    textStyle: { fontSize: "28px", fontWeight: "bold", textAlign: "center" },
    placeholder: "NOVIDADE! 🎉",
    icon: "📢"
  },
  {
    id: 6,
    name: "Gratidão",
    background: "linear-gradient(135deg, #FDC830 0%, #F37335 100%)",
    textStyle: { fontSize: "22px", textAlign: "center" },
    placeholder: "Sou grato por...",
    icon: "🙏"
  },
  {
    id: 7,
    name: "Humor",
    background: "linear-gradient(135deg, #a8ff78 0%, #78ffd6 100%)",
    textStyle: { fontSize: "26px", textAlign: "center" },
    placeholder: "😂 Algo engraçado...",
    icon: "😂"
  },
  {
    id: 8,
    name: "Reflexão",
    background: "linear-gradient(135deg, #2C3E50 0%, #4CA1AF 100%)",
    textStyle: { fontSize: "20px", fontStyle: "italic", textAlign: "center" },
    placeholder: "Pensando sobre...",
    icon: "💭"
  },
];

export default function CreateStory({ user, onClose, onCreated }) {
  const [step, setStep] = useState("select"); // select, text, media, preview
  const [storyType, setStoryType] = useState(null);
  const [text, setText] = useState("");
  const [background, setBackground] = useState(backgrounds[0].style);
  const [media, setMedia] = useState(null);
  const [mediaPreview, setMediaPreview] = useState(null);
  const [mediaType, setMediaType] = useState(null);
  const [caption, setCaption] = useState("");
  const [privacy, setPrivacy] = useState("friends");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [uploadProgress, setUploadProgress] = useState(0);

  // New states for effects, music, and templates
  const [selectedEffect, setSelectedEffect] = useState(effects[0]);
  const [selectedMusic, setSelectedMusic] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [showEffects, setShowEffects] = useState(false);
  const [showMusic, setShowMusic] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);
  const [isPlayingPreview, setIsPlayingPreview] = useState(false);
  const [textStyle, setTextStyle] = useState({});

  const fileInputRef = useRef(null);
  const audioInputRef = useRef(null);
  const audioPreviewRef = useRef(null);

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const isImage = file.type.startsWith("image/");
    const isVideo = file.type.startsWith("video/");

    if (!isImage && !isVideo) {
      setError("Selecione uma imagem ou video");
      return;
    }

    // Imagens e vídeos sem limite de tamanho

    setMedia(file);
    setMediaType(isImage ? "image" : "video");
    setStoryType(isImage ? "image" : "video");

    const reader = new FileReader();
    reader.onload = (e) => {
      setMediaPreview(e.target.result);
      setStep("media");
    };
    reader.readAsDataURL(file);
    setError("");
  };

  const handleTextStory = () => {
    setStoryType("text");
    setStep("text");
  };

  // Handle template selection
  const handleTemplateSelect = (template) => {
    setSelectedTemplate(template);
    setBackground(template.background);
    setTextStyle(template.textStyle);
    setText("");
    setShowTemplates(false);
  };

  // Handle effect selection
  const handleEffectSelect = (effect) => {
    setSelectedEffect(effect);
  };

  // Handle music selection
  const handleMusicSelect = (music) => {
    // Stop current preview if playing
    if (audioPreviewRef.current) {
      audioPreviewRef.current.pause();
      audioPreviewRef.current = null;
    }
    setSelectedMusic(music);
    setShowMusic(false);
    setIsPlayingPreview(false);
  };

  // Preview music
  const previewMusic = (music, e) => {
    e.stopPropagation();

    if (audioPreviewRef.current) {
      audioPreviewRef.current.pause();
    }

    if (isPlayingPreview && selectedMusic?.id === music.id) {
      setIsPlayingPreview(false);
      return;
    }

    const audio = new Audio(music.url);
    audioPreviewRef.current = audio;
    audio.play().catch(() => {
      // Handle autoplay restrictions
      console.log("Audio preview not available");
    });
    setIsPlayingPreview(true);

    audio.onended = () => {
      setIsPlayingPreview(false);
    };
  };

  // Handle custom audio upload
  const handleAudioUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith("audio/")) {
      setError("Selecione um arquivo de áudio");
      return;
    }

    const audioUrl = URL.createObjectURL(file);
    const customMusic = {
      id: "custom",
      title: file.name.replace(/\.[^/.]+$/, ""),
      artist: "Seu áudio",
      duration: "...",
      url: audioUrl,
      file: file
    };
    setSelectedMusic(customMusic);
    setShowMusic(false);
  };

  // Remove selected music
  const removeMusic = () => {
    if (audioPreviewRef.current) {
      audioPreviewRef.current.pause();
    }
    setSelectedMusic(null);
    setIsPlayingPreview(false);
  };

  // Cleanup audio on unmount
  useEffect(() => {
    return () => {
      if (audioPreviewRef.current) {
        audioPreviewRef.current.pause();
      }
    };
  }, []);

  const handleSubmit = async () => {
    setLoading(true);
    setError("");
    setUploadProgress(0);

    try {
      let storyData = { type: storyType, privacy };

      if (storyType === "text") {
        if (!text.trim()) {
          setError("Digite algo para seu story");
          setLoading(false);
          return;
        }
        storyData.text = text;
        storyData.background = background;
        if (selectedTemplate) {
          storyData.fontStyle = JSON.stringify(textStyle);
        }
      } else {
        if (!media) {
          setError("Selecione uma midia");
          setLoading(false);
          return;
        }

        // Upload media via backend
        const uploadResult = await uploadStoryMedia(
          media,
          user.token,
          (progress) => setUploadProgress(progress)
        );

        if (uploadResult.status !== "ok") {
          setError(uploadResult.message || "Erro ao fazer upload");
          setLoading(false);
          return;
        }

        // Get the uploaded media URL
        const uploadedMedia = uploadResult.data[0];
        storyData.media = uploadedMedia.url;

        if (caption.trim()) {
          storyData.text = caption;
        }

        // Add effect info if applied
        if (selectedEffect && selectedEffect.id !== "none") {
          storyData.effect = selectedEffect.id;
        }
      }

      // Add music if selected
      if (selectedMusic) {
        storyData.music = {
          title: selectedMusic.title,
          artist: selectedMusic.artist,
          url: selectedMusic.url
        };
      }

      const res = await createStory(storyData, user.token);

      if (res.status === "ok") {
        onCreated();
      } else {
        setError(res.message || "Erro ao criar story");
      }
    } catch (err) {
      console.error("Error creating story:", err);
      setError("Erro ao criar story. Tente novamente.");
    }

    setLoading(false);
  };

  const goBack = () => {
    if (step === "text" || step === "media") {
      setStep("select");
      setStoryType(null);
      setMedia(null);
      setMediaPreview(null);
      setText("");
      setError("");
      setSelectedEffect(effects[0]);
      setSelectedMusic(null);
      setSelectedTemplate(null);
      setTextStyle({});
      setShowEffects(false);
      setShowMusic(false);
      setShowTemplates(false);
      if (audioPreviewRef.current) {
        audioPreviewRef.current.pause();
      }
    } else {
      onClose();
    }
  };

  return (
    <div className="create_story_overlay" onClick={onClose}>
      <div className="create_story_container" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="create_story_header">
          <button className="create_story_back" onClick={goBack}>
            {step === "select" ? (
              <svg viewBox="0 0 24 24" width="24" height="24">
                <path fill="currentColor" d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
              </svg>
            ) : (
              <svg viewBox="0 0 24 24" width="24" height="24">
                <path fill="currentColor" d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z"/>
              </svg>
            )}
          </button>
          <h2>Criar story</h2>
          {(step === "text" || step === "media") && (
            <button
              className="create_story_share"
              onClick={handleSubmit}
              disabled={loading || (storyType === "text" && !text.trim())}
            >
              {loading ? (
                <span className="share_loading">
                  {uploadProgress > 0 ? `${uploadProgress}%` : "..."}
                </span>
              ) : (
                "Compartilhar"
              )}
            </button>
          )}
        </div>

        {/* Content */}
        <div className="create_story_body">
          {/* Step 1: Select Type */}
          {step === "select" && (
            <div className="create_story_select">
              <div className="create_story_user">
                <img src={user.picture} alt="" />
                <span>{user.first_name} {user.last_name}</span>
              </div>

              <div className="create_story_options">
                <div className="create_story_option" onClick={handleTextStory}>
                  <div className="option_preview text_option">
                    <span>Aa</span>
                  </div>
                  <div className="option_info">
                    <h3>Criar story de texto</h3>
                    <p>Compartilhe o que voce esta pensando</p>
                  </div>
                </div>

                <div className="create_story_option" onClick={() => fileInputRef.current?.click()}>
                  <div className="option_preview media_option">
                    <svg viewBox="0 0 24 24" width="32" height="32">
                      <path fill="currentColor" d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z"/>
                    </svg>
                  </div>
                  <div className="option_info">
                    <h3>Criar story de foto/video</h3>
                    <p>Compartilhe uma foto ou video</p>
                  </div>
                </div>
              </div>

              <input
                ref={fileInputRef}
                type="file"
                accept="image/*,video/*"
                onChange={handleFileSelect}
                hidden
              />
            </div>
          )}

          {/* Step 2: Text Story */}
          {step === "text" && (
            <div className="create_story_text">
              <div className="text_story_preview" style={{ background }}>
                <textarea
                  placeholder={selectedTemplate?.placeholder || "Comece a digitar..."}
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  maxLength={500}
                  autoFocus
                  style={textStyle}
                />
              </div>

              {/* Toolbar for text story */}
              <div className="story_toolbar">
                <button
                  className={`toolbar_btn ${showTemplates ? "active" : ""}`}
                  onClick={() => { setShowTemplates(!showTemplates); setShowMusic(false); }}
                  title="Modelos"
                >
                  <svg viewBox="0 0 24 24" width="22" height="22">
                    <path fill="currentColor" d="M4 5h16v2H4V5zm0 6h16v2H4v-2zm0 6h16v2H4v-2z"/>
                  </svg>
                  <span>Modelos</span>
                </button>
                <button
                  className={`toolbar_btn ${showMusic ? "active" : ""} ${selectedMusic ? "has_selection" : ""}`}
                  onClick={() => { setShowMusic(!showMusic); setShowTemplates(false); }}
                  title="Música"
                >
                  <svg viewBox="0 0 24 24" width="22" height="22">
                    <path fill="currentColor" d="M12 3v10.55c-.59-.34-1.27-.55-2-.55-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4V7h4V3h-6z"/>
                  </svg>
                  <span>Música</span>
                </button>
              </div>

              {/* Templates Panel */}
              {showTemplates && (
                <div className="templates_panel">
                  <h4>Escolha um modelo</h4>
                  <div className="templates_grid">
                    {templates.map((template) => (
                      <button
                        key={template.id}
                        className={`template_item ${selectedTemplate?.id === template.id ? "selected" : ""}`}
                        onClick={() => handleTemplateSelect(template)}
                      >
                        <div
                          className="template_preview"
                          style={{ background: template.background }}
                        >
                          <span className="template_icon">{template.icon}</span>
                        </div>
                        <span className="template_name">{template.name}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Music Panel */}
              {showMusic && (
                <div className="music_panel">
                  <h4>Adicionar música</h4>
                  <button
                    className="upload_audio_btn"
                    onClick={() => audioInputRef.current?.click()}
                  >
                    <svg viewBox="0 0 24 24" width="20" height="20">
                      <path fill="currentColor" d="M9 16h6v-6h4l-7-7-7 7h4v6zm-4 2h14v2H5v-2z"/>
                    </svg>
                    Usar seu áudio
                  </button>
                  <input
                    ref={audioInputRef}
                    type="file"
                    accept="audio/*"
                    onChange={handleAudioUpload}
                    hidden
                  />
                  <div className="music_list">
                    {musicLibrary.map((music) => (
                      <div
                        key={music.id}
                        className={`music_item ${selectedMusic?.id === music.id ? "selected" : ""}`}
                        onClick={() => handleMusicSelect(music)}
                      >
                        <div className="music_info">
                          <span className="music_title">{music.title}</span>
                          <span className="music_artist">{music.artist}</span>
                        </div>
                        <div className="music_actions">
                          <span className="music_duration">{music.duration}</span>
                          <button
                            className="music_preview_btn"
                            onClick={(e) => previewMusic(music, e)}
                          >
                            {isPlayingPreview && selectedMusic?.id === music.id ? (
                              <svg viewBox="0 0 24 24" width="18" height="18">
                                <path fill="currentColor" d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"/>
                              </svg>
                            ) : (
                              <svg viewBox="0 0 24 24" width="18" height="18">
                                <path fill="currentColor" d="M8 5v14l11-7z"/>
                              </svg>
                            )}
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Selected Music Badge */}
              {selectedMusic && !showMusic && (
                <div className="selected_music_badge">
                  <svg viewBox="0 0 24 24" width="16" height="16">
                    <path fill="currentColor" d="M12 3v10.55c-.59-.34-1.27-.55-2-.55-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4V7h4V3h-6z"/>
                  </svg>
                  <span>{selectedMusic.title}</span>
                  <button onClick={removeMusic}>
                    <svg viewBox="0 0 24 24" width="16" height="16">
                      <path fill="currentColor" d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
                    </svg>
                  </button>
                </div>
              )}

              <div className="text_story_options">
                <div className="background_selector">
                  <span>Fundo:</span>
                  <div className="background_options">
                    {backgrounds.map((bg) => (
                      <button
                        key={bg.id}
                        className={`bg_option ${bg.style === background ? "selected" : ""}`}
                        style={{ background: bg.style }}
                        onClick={() => { setBackground(bg.style); setSelectedTemplate(null); }}
                      />
                    ))}
                  </div>
                </div>

                <div className="privacy_selector">
                  <span>Quem pode ver:</span>
                  <select value={privacy} onChange={(e) => setPrivacy(e.target.value)}>
                    <option value="everyone">Todos</option>
                    <option value="friends">Amigos</option>
                  </select>
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Media Story */}
          {step === "media" && (
            <div className="create_story_media">
              <div className="media_story_preview">
                {mediaType === "image" ? (
                  <img
                    src={mediaPreview}
                    alt=""
                    style={{ filter: selectedEffect.filter }}
                  />
                ) : (
                  <video
                    src={mediaPreview}
                    controls
                    style={{ filter: selectedEffect.filter }}
                  />
                )}
                <button
                  className="change_media"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <svg viewBox="0 0 24 24" width="20" height="20">
                    <path fill="currentColor" d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z"/>
                  </svg>
                  Alterar
                </button>

                {/* Effect indicator */}
                {selectedEffect.id !== "none" && (
                  <div className="effect_indicator">
                    <span>✨ {selectedEffect.name}</span>
                  </div>
                )}
              </div>

              {/* Toolbar for media story */}
              <div className="story_toolbar">
                <button
                  className={`toolbar_btn ${showEffects ? "active" : ""}`}
                  onClick={() => { setShowEffects(!showEffects); setShowMusic(false); }}
                  title="Efeitos"
                >
                  <svg viewBox="0 0 24 24" width="22" height="22">
                    <path fill="currentColor" d="M19.36 10.04C18.67 6.59 15.64 4 12 4 9.11 4 6.6 5.64 5.35 8.04 2.34 8.36 0 10.91 0 14c0 3.31 2.69 6 6 6h13c2.76 0 5-2.24 5-5 0-2.64-2.05-4.78-4.64-4.96z"/>
                  </svg>
                  <span>Efeitos</span>
                </button>
                <button
                  className={`toolbar_btn ${showMusic ? "active" : ""} ${selectedMusic ? "has_selection" : ""}`}
                  onClick={() => { setShowMusic(!showMusic); setShowEffects(false); }}
                  title="Música"
                >
                  <svg viewBox="0 0 24 24" width="22" height="22">
                    <path fill="currentColor" d="M12 3v10.55c-.59-.34-1.27-.55-2-.55-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4V7h4V3h-6z"/>
                  </svg>
                  <span>Música</span>
                </button>
              </div>

              {/* Effects Panel */}
              {showEffects && (
                <div className="effects_panel">
                  <h4>Escolha um efeito</h4>
                  <div className="effects_grid">
                    {effects.map((effect) => (
                      <button
                        key={effect.id}
                        className={`effect_item ${selectedEffect.id === effect.id ? "selected" : ""}`}
                        onClick={() => handleEffectSelect(effect)}
                      >
                        <div
                          className="effect_preview"
                          style={{
                            backgroundImage: `url(${mediaPreview})`,
                            filter: effect.filter
                          }}
                        />
                        <span className="effect_name">{effect.name}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Music Panel for Media */}
              {showMusic && (
                <div className="music_panel">
                  <h4>Adicionar música</h4>
                  <button
                    className="upload_audio_btn"
                    onClick={() => audioInputRef.current?.click()}
                  >
                    <svg viewBox="0 0 24 24" width="20" height="20">
                      <path fill="currentColor" d="M9 16h6v-6h4l-7-7-7 7h4v6zm-4 2h14v2H5v-2z"/>
                    </svg>
                    Usar seu áudio
                  </button>
                  <input
                    ref={audioInputRef}
                    type="file"
                    accept="audio/*"
                    onChange={handleAudioUpload}
                    hidden
                  />
                  <div className="music_list">
                    {musicLibrary.map((music) => (
                      <div
                        key={music.id}
                        className={`music_item ${selectedMusic?.id === music.id ? "selected" : ""}`}
                        onClick={() => handleMusicSelect(music)}
                      >
                        <div className="music_info">
                          <span className="music_title">{music.title}</span>
                          <span className="music_artist">{music.artist}</span>
                        </div>
                        <div className="music_actions">
                          <span className="music_duration">{music.duration}</span>
                          <button
                            className="music_preview_btn"
                            onClick={(e) => previewMusic(music, e)}
                          >
                            {isPlayingPreview && selectedMusic?.id === music.id ? (
                              <svg viewBox="0 0 24 24" width="18" height="18">
                                <path fill="currentColor" d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"/>
                              </svg>
                            ) : (
                              <svg viewBox="0 0 24 24" width="18" height="18">
                                <path fill="currentColor" d="M8 5v14l11-7z"/>
                              </svg>
                            )}
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Selected Music Badge */}
              {selectedMusic && !showMusic && (
                <div className="selected_music_badge">
                  <svg viewBox="0 0 24 24" width="16" height="16">
                    <path fill="currentColor" d="M12 3v10.55c-.59-.34-1.27-.55-2-.55-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4V7h4V3h-6z"/>
                  </svg>
                  <span>{selectedMusic.title}</span>
                  <button onClick={removeMusic}>
                    <svg viewBox="0 0 24 24" width="16" height="16">
                      <path fill="currentColor" d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
                    </svg>
                  </button>
                </div>
              )}

              <div className="media_story_options">
                <input
                  type="text"
                  placeholder="Adicionar legenda (opcional)"
                  value={caption}
                  onChange={(e) => setCaption(e.target.value)}
                  className="caption_input"
                />

                <div className="privacy_selector">
                  <span>Quem pode ver:</span>
                  <select value={privacy} onChange={(e) => setPrivacy(e.target.value)}>
                    <option value="everyone">Todos</option>
                    <option value="friends">Amigos</option>
                  </select>
                </div>
              </div>

              <input
                ref={fileInputRef}
                type="file"
                accept="image/*,video/*"
                onChange={handleFileSelect}
                hidden
              />
            </div>
          )}

          {/* Error message */}
          {error && (
            <div className="create_story_error">
              <svg viewBox="0 0 24 24" width="20" height="20">
                <path fill="currentColor" d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
              </svg>
              {error}
            </div>
          )}

          {/* Loading progress */}
          {loading && uploadProgress > 0 && (
            <div className="upload_progress">
              <div className="progress_bar" style={{ width: `${uploadProgress}%` }} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
